(function () {
  if (window.__GWP_BOOTED__) return;
  window.__GWP_BOOTED__ = true;

  const nativeFetch = window.fetch.bind(window);

  const VERIFY_TRIES = 8;
  const VERIFY_DELAY_MS = 400;
  const FETCH_INTERCEPT_DELAY_MS = 800;
  const AVAILABILITY_TTL_MS = 30000;

  const TIER_GIFT = 'tier';
  const PRODUCT_GIFT = 'product';

  const root = window.Shopify?.routes?.root ?? '/';
  const R = window.routes ?? {};
  const CART_URL = `${R.cart_url ?? root + 'cart'}.js`;
  const ADD_URL = `${R.cart_add_url ?? root + 'cart/add'}.js`;
  const CHANGE_URL = `${R.cart_change_url ?? root + 'cart/change'}.js`;
  const UPDATE_URL = `${R.cart_update_url ?? root + 'cart/update'}.js`;
  const SECTIONS_URL = `${root}?sections=cart-drawer,cart-icon-bubble`;

  const CHECK_SVG = `<svg viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <path d="M12.6 3.6 5.9 11.3 2.4 7.9" stroke="#3A3A3A" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;

  const JSON_HEADERS = { 'Content-Type': 'application/json' };

  function fetchCart() {
    return nativeFetch(CART_URL).then((r) => r.json());
  }

  function postJSON(url, payload) {
    return nativeFetch(url, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(payload),
    });
  }

  function productGid(productId) {
    return `gid://shopify/Product/${productId}`;
  }

  function giftKind(item) {
    const kind = item.properties?._gwp;
    return kind === TIER_GIFT || kind === PRODUCT_GIFT ? kind : null;
  }

  // Post-discount spend. final_line_price already has product discounts taken
  // off; order discounts (e.g. $20 off the order) are only on the cart, so
  // they're subtracted here. Gift lines are $0, so no order discount lands on them.
  // The WWT tin is gift-only and never counts, even while it is still full price.
  function qualifyingSpend(cart) {
    const lines = cart.items
      .filter((i) => !i.properties?._gwp && String(i.variant_id) !== wwtGiftVariantId)
      .reduce((sum, i) => sum + i.final_line_price, 0);
    const orderDiscounts = (cart.cart_level_discount_applications ?? []).reduce(
      (sum, d) => sum + (d.total_allocated_amount ?? 0),
      0,
    );
    return Math.max(0, lines - orderDiscounts);
  }

  function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  function formatMoney(cents) {
    return (cents / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: window.Shopify?.currency?.active ?? 'USD',
    });
  }

  function formatWholeMoney(cents) {
    return (cents / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: window.Shopify?.currency?.active ?? 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }

  function currencyRate() {
    const rate = parseFloat(window.Shopify?.currency?.rate);
    return rate > 0 ? rate : 1;
  }

  function thresholdCents(tier) {
    return Math.round(parseFloat(tier.threshold) * currencyRate()) * 100;
  }

  function sortedTiers() {
    const tiers = window.__GWP_CONFIG__;
    if (!Array.isArray(tiers)) return [];
    return tiers
      .filter((t) => t.threshold !== '' && !isNaN(parseFloat(t.threshold)))
      .sort((a, b) => parseFloat(a.threshold) - parseFloat(b.threshold));
  }

  async function fetchProductInfo(handle) {
    if (!handle) return null;
    try {
      const res = await nativeFetch(`${root}products/${handle}.js`);
      if (!res.ok) return null;
      const product = await res.json();
      const availableVariantIds = product.variants.filter((v) => v.available).map((v) => String(v.id));
      return {
        available: availableVariantIds.length > 0,
        variantId: availableVariantIds[0] ?? null,
        availableVariantIds,
        title: product.title,
      };
    } catch (e) {
      return null;
    }
  }

  let productAvailability = new Map();

  function tierCandidates(tier) {
    const ids = [tier.giftProductId, ...(tier.backupGiftProductIds ?? [])];
    const handles = [tier.giftProductHandle, ...(tier.backupGiftProductHandles ?? [])];
    return ids.map((productId, i) => ({ productId, handle: handles[i] })).filter((c) => c.productId && c.handle);
  }

  let availabilityKey = '';
  let availabilityFetchedAt = 0;

  async function refreshTierAvailability() {
    const handles = [...new Set(sortedTiers().flatMap((t) => tierCandidates(t).map((c) => c.handle)))];
    const key = handles.join(',');
    if (key === availabilityKey && Date.now() - availabilityFetchedAt < AVAILABILITY_TTL_MS) return;

    const entries = await Promise.all(handles.map(async (h) => [h, await fetchProductInfo(h)]));
    const known = entries.filter(([, info]) => info);
    productAvailability = new Map(known);
    availabilityKey = key;
    availabilityFetchedAt = known.length === handles.length ? Date.now() : 0;
  }

  function invalidateAvailability() {
    availabilityFetchedAt = 0;
  }

  // Disappear mode
  function activeTiers() {
    return sortedTiers().filter((tier) => {
      const candidates = tierCandidates(tier);
      if (!candidates.length) return true;
      return candidates.some((c) => productAvailability.get(c.handle)?.available !== false);
    });
  }

  // Replacement mode
  function chooseTierGift(tier, cart) {
    const available = tierCandidates(tier)
      .map((c) => ({ ...c, info: productAvailability.get(c.handle) }))
      .filter((c) => c.info?.available);
    if (!available.length) return null;

    const tierLines = (cart?.items ?? []).filter((i) => giftKind(i) === TIER_GIFT);
    for (const c of available) {
      const line = tierLines.find((i) => productGid(i.product_id) === c.productId);
      if (!line) continue;
      const variantId = String(line.variant_id);
      return {
        variantId: c.info.availableVariantIds.includes(variantId) ? variantId : c.info.variantId,
        title: c.info.title,
      };
    }

    return { variantId: available[0].info.variantId, title: available[0].info.title };
  }

  function withThreshold(text, tier) {
    return (text ?? '').replaceAll('{{threshold}}', formatWholeMoney(thresholdCents(tier)));
  }

  function withGift(text, tier, cart) {
    return withThreshold(text, tier).replaceAll('{{gift}}', chooseTierGift(tier, cart)?.title ?? '');
  }

  function readTierAvailability() {
    const map = new Map();
    document.querySelectorAll('[data-gwp-tier-gift]').forEach((el) => {
      const variantId = el.dataset.variantId;
      if (!variantId) return;
      map.set(String(variantId), {
        available: el.dataset.available === 'true',
        title: el.dataset.title ?? '',
      });
    });
    return map;
  }

  async function waitForCart(predicate) {
    for (let i = 0; i < VERIFY_TRIES; i++) {
      const cart = await fetchCart();
      if (predicate(cart)) return cart;
      await sleep(VERIFY_DELAY_MS);
    }
    return fetchCart();
  }

  function giftKey(kind, variantId, qualifierGid) {
    return kind === PRODUCT_GIFT ? `${PRODUCT_GIFT}|${variantId}|${qualifierGid}` : `${TIER_GIFT}|${variantId}`;
  }

  function lineKey(item) {
    const kind = giftKind(item);
    if (!kind) return null;
    return giftKey(kind, item.variant_id, item.properties?._gwp_for ?? '');
  }

  function reorderProductGiftRows() {
    const tbody = document.querySelector('#CartDrawer-CartItems tbody');
    if (!tbody) return;

    const rows = Array.from(tbody.children);
    if (!rows.length) return;

    const rowItems = rows
      .map((row) => ({ row, index: Number(row.id?.replace('CartDrawer-Item-', '')) - 1 }))
      .sort((a, b) => a.index - b.index)
      .map(({ row }) => ({ row, kind: giftKind({ properties: { _gwp: row.dataset.gwp } }) }));

    const tierGiftRows = rowItems.filter(({ kind }) => kind === TIER_GIFT).map(({ row }) => row);

    const qualifierRows = new Map();

    rowItems.forEach(({ kind, row }) => {
      if (kind || !row.dataset.productId) return;

      const qualifierGid = productGid(row.dataset.productId);

      if (!qualifierRows.has(qualifierGid)) {
        qualifierRows.set(qualifierGid, row);
      }
    });

    const giftsByQualifierRow = new Map();
    const movedRows = new Set();

    rowItems.forEach(({ kind, row }) => {
      if (kind !== PRODUCT_GIFT) return;

      const qualifierGid = String(row.dataset.gwpFor ?? '');
      const qualifierRow = qualifierRows.get(qualifierGid);

      if (!qualifierRow) return;

      if (!giftsByQualifierRow.has(qualifierRow)) {
        giftsByQualifierRow.set(qualifierRow, []);
      }

      giftsByQualifierRow.get(qualifierRow).push(row);
      movedRows.add(row);
    });

    if (!tierGiftRows.length && !movedRows.size) return;

    const tierRowsSet = new Set(tierGiftRows);

    const ordered = [...tierGiftRows];

    rowItems.forEach(({ row }) => {
      if (tierRowsSet.has(row) || movedRows.has(row)) return;

      ordered.push(row);
      ordered.push(...(giftsByQualifierRow.get(row) ?? []));
    });

    if (ordered.every((row, i) => rows[i] === row)) return;

    const fragment = document.createDocumentFragment();
    ordered.forEach((row) => fragment.appendChild(row));
    tbody.appendChild(fragment);
  }

  // GWP 1. Tiers accumulate
  function earnedTierGifts(spendCents, tiers, cart) {
    const earned = new Map();
    for (const tier of tiers) {
      if (spendCents < thresholdCents(tier)) continue;
      const gift = chooseTierGift(tier, cart);
      if (!gift?.variantId) continue;
      const qty = Math.max(1, parseInt(tier.maxQuantity ?? 1, 10) || 1);
      earned.set(gift.variantId, Math.max(earned.get(gift.variantId) ?? 0, qty));
    }
    return earned;
  }

  // GWP 2. Rendered by gwp-product-gifts.liquid
  function readProductGiftMap() {
    const map = new Map();
    document.querySelectorAll('[data-gwp-qualifier]').forEach((el) => {
      const qualifier = el.dataset.gwpQualifier;
      const variantId = el.dataset.gwpGiftVariant;
      if (qualifier && variantId) map.set(String(qualifier), String(variantId));
    });
    return map;
  }

  function desiredGifts(cart, tiers) {
    const desired = new Map();

    const spend = qualifyingSpend(cart);
    for (const [variantId, quantity] of earnedTierGifts(spend, tiers, cart)) {
      desired.set(giftKey(TIER_GIFT, variantId), {
        variantId,
        quantity,
        properties: { _gwp: TIER_GIFT },
      });
    }

    const giftMap = readProductGiftMap();
    if (giftMap.size) {
      const qualifying = new Map();
      for (const item of cart.items) {
        if (item.properties?._gwp) continue;
        const productId = String(item.product_id);
        if (!giftMap.has(productId)) continue;
        qualifying.set(productId, (qualifying.get(productId) ?? 0) + item.quantity);
      }

      for (const [productId, quantity] of qualifying) {
        const variantId = giftMap.get(productId);
        const qualifierGid = productGid(productId);
        desired.set(giftKey(PRODUCT_GIFT, variantId, qualifierGid), {
          variantId,
          quantity,
          properties: { _gwp: PRODUCT_GIFT, _gwp_for: qualifierGid },
        });
      }
    }

    return desired;
  }

  async function addGift(key, spec) {
    for (let quantity = spec.quantity; quantity > 0; quantity--) {
      let ok = false;
      try {
        const res = await postJSON(ADD_URL, {
          items: [{ id: Number(spec.variantId), quantity, properties: spec.properties }],
        });
        ok = res.ok;
      } catch (e) {
        ok = false;
      }
      if (!ok) continue;

      await waitForCart((cart) => cart.items.some((i) => lineKey(i) === key));
      return true;
    }
    return false;
  }

  async function moveGiftQuantity(line, target) {
    for (let quantity = target; quantity >= 0; quantity--) {
      let ok = false;
      try {
        const res = await postJSON(CHANGE_URL, { id: line.key, quantity });
        ok = res.ok;
      } catch (e) {
        ok = false;
      }
      if (!ok) continue;

      await waitForCart((cart) => {
        const found = cart.items.find((i) => i.key === line.key);
        return quantity === 0 ? !found : found?.quantity === quantity;
      });
      return true;
    }
    return false;
  }

  async function syncGifts(cart) {
    const tiers = activeTiers();
    cart = cart ?? (await fetchCart());
    const desired = desiredGifts(cart, tiers);

    const existing = new Map();
    for (const item of cart.items) {
      const key = lineKey(item);
      if (!key) continue;
      if (!existing.has(key)) existing.set(key, []);
      existing.get(key).push(item);
    }

    if (!desired.size && !existing.size) return false;

    let changed = false;

    const decreases = [];
    for (const [key, lines] of existing) {
      const want = desired.get(key)?.quantity ?? 0;
      for (let i = 0; i < lines.length; i++) {
        const target = i === 0 ? want : 0;
        if (lines[i].quantity === target) continue;
        if (target < lines[i].quantity) decreases.push([lines[i], target]);
        else if (await moveGiftQuantity(lines[i], target)) changed = true;
      }
    }
    if (decreases.length && (await decreaseGiftLines(decreases))) changed = true;

    const adds = [...desired].filter(([key]) => !existing.has(key));
    if (adds.length && (await addGifts(adds))) changed = true;

    return changed;
  }

  async function decreaseGiftLines(changes) {
    if (changes.length > 1) {
      try {
        const updates = Object.fromEntries(changes.map(([line, target]) => [line.key, target]));
        const res = await postJSON(UPDATE_URL, { updates });
        if (res.ok) {
          await waitForCart((cart) =>
            changes.every(([line, target]) => {
              const found = cart.items.find((i) => i.key === line.key);
              return target === 0 ? !found : found?.quantity === target;
            }),
          );
          return true;
        }
      } catch (e) {
        /* fall back to one at a time */
      }
    }

    let changed = false;
    for (const [line, target] of changes) {
      if (await moveGiftQuantity(line, target)) changed = true;
    }
    return changed;
  }

  async function addGifts(adds) {
    if (adds.length > 1) {
      try {
        const res = await postJSON(ADD_URL, {
          items: adds.map(([, spec]) => ({
            id: Number(spec.variantId),
            quantity: spec.quantity,
            properties: spec.properties,
          })),
        });
        if (res.ok) {
          await waitForCart((cart) => adds.every(([key]) => cart.items.some((i) => lineKey(i) === key)));
          return true;
        }
      } catch (e) {
        /* fall back to one at a time */
      }
    }

    const present = adds.length > 1 ? (await fetchCart()).items.map(lineKey) : [];
    let changed = false;
    for (const [key, spec] of adds) {
      if (present.includes(key)) {
        changed = true;
        continue;
      }
      if (await addGift(key, spec)) changed = true;
      else if (spec.properties._gwp === TIER_GIFT) invalidateAvailability();
    }
    return changed;
  }

  function anchorPercent(index, count) {
    return ((2 * index + 1) / (2 * count)) * 100;
  }

  function fillPercent(spend, tiers) {
    const n = tiers.length;
    const cents = tiers.map(thresholdCents);

    if (spend >= cents[n - 1]) return 100;
    if (spend < cents[0]) {
      return cents[0] > 0 ? (spend / cents[0]) * anchorPercent(0, n) : 0;
    }
    for (let i = 0; i < n - 1; i++) {
      if (spend < cents[i + 1]) {
        const span = cents[i + 1] - cents[i];
        const t = span > 0 ? (spend - cents[i]) / span : 0;
        return anchorPercent(i, n) + t * (anchorPercent(i + 1, n) - anchorPercent(i, n));
      }
    }
    return 100;
  }

  function messageFor(spend, tiers, cart) {
    const next = tiers.find((t) => spend < thresholdCents(t));
    if (next) {
      return withGift(next.progressTextBefore, next, cart).replace(
        '{{amount}}',
        formatMoney(thresholdCents(next) - spend),
      );
    }
    const last = tiers[tiers.length - 1];
    return last ? withGift(last.progressTextAfter, last, cart) : '';
  }

  function barMarkup(spend, tiers, cart, startPercent) {
    const text = messageFor(spend, tiers, cart);
    const message = text ? `<div class="gwp-bar__message">${text}</div>` : '';

    const tiersHTML = tiers
      .map((tier) => {
        const unlocked = spend >= thresholdCents(tier);
        return `<div class="gwp-bar__tier">
          <div class="gwp-bar__check${unlocked ? ' is-unlocked' : ''}">${CHECK_SVG}</div>
          <span class="gwp-bar__label">${withGift(tier.label, tier, cart)}</span>
        </div>`;
      })
      .join('');

    return `${message}
      <div class="gwp-bar__progress">
        <div class="gwp-bar__track">
          <div class="gwp-bar__fill" style="width:${startPercent}%;"></div>
        </div>
        <div class="gwp-bar__tiers">${tiersHTML}</div>
      </div>`;
  }

  function applyMessage(container, text) {
    const existing = container.querySelector('.gwp-bar__message');
    if (!text) {
      existing?.remove();
      return;
    }
    if (existing) {
      if (existing.innerHTML !== text) existing.innerHTML = text;
      return;
    }
    const el = document.createElement('div');
    el.className = 'gwp-bar__message';
    el.innerHTML = text;
    container.prepend(el);
  }

  let lastFillPercent = 0;
  let lastBarHTML = '';
  let lastBarClass = '';

  function restoreBar(container) {
    if (!lastBarHTML || container.innerHTML.trim()) return;
    container.innerHTML = lastBarHTML;
    container.className = lastBarClass;
  }

  function applyBar(container, spend, tiers, cart) {
    const target = fillPercent(spend, tiers);
    const tierEls = container.querySelectorAll('.gwp-bar__tier');
    let fill = container.querySelector('.gwp-bar__fill');

    if (!fill || tierEls.length !== tiers.length) {
      container.innerHTML = barMarkup(spend, tiers, cart, lastFillPercent);
      fill = container.querySelector('.gwp-bar__fill');

      if (fill && Math.abs(target - lastFillPercent) > 0.01) {
        void fill.offsetWidth;
        requestAnimationFrame(() => {
          fill.style.width = `${target}%`;
        });
      }
    } else {
      fill.style.width = `${target}%`;

      tiers.forEach((tier, i) => {
        const unlocked = spend >= thresholdCents(tier);
        tierEls[i].querySelector('.gwp-bar__check')?.classList.toggle('is-unlocked', unlocked);

        const label = tierEls[i].querySelector('.gwp-bar__label');
        const text = withGift(tier.label, tier, cart);
        if (label && label.textContent !== text) label.textContent = text;
      });

      applyMessage(container, messageFor(spend, tiers, cart));
    }

    container.classList.toggle('is-complete', spend >= thresholdCents(tiers[tiers.length - 1]));

    lastFillPercent = target;
    lastBarHTML = barMarkup(spend, tiers, cart, target);
    lastBarClass = container.className;
  }

  function tierForProduct(productId) {
    const gid = productGid(productId);
    return sortedTiers().find((t) => tierCandidates(t).some((c) => c.productId === gid));
  }

  function applyGiftCopy() {
    document.querySelectorAll('.cart-item__gift-copy[data-product-id]').forEach((el) => {
      const tier = tierForProduct(el.dataset.productId);
      const text = tier?.cartItemCopy ?? '';
      el.textContent = tier
        ? withThreshold(text, tier).replace('{{amount}}', formatMoney(thresholdCents(tier)))
        : '';
    });
  }

  function updateShippingRow(spend, tiers) {
    const shipping = tiers.find((t) => !(t.giftVariantIds ?? []).length);
    const rows = document.querySelectorAll('#CartDrawer-ShippingValue');
    if (!rows.length) return;

    const free = shipping && spend >= thresholdCents(shipping);
    rows.forEach((el) => {
      el.textContent = free ? 'FREE' : 'Calculated at checkout';
      el.classList.toggle('is-free', Boolean(free));
    });
  }

  let lastChipCart = null;

  function renderDiscountChips(cart) {
    lastChipCart = cart;
    const codes = cart.discount_codes ?? [];
    const key = codes.map((d) => `${d.code}:${d.applicable}`).join(',');

    document.querySelectorAll('[data-gwp-discounts-row]').forEach((row) => {
      row.hidden = !codes.length && !cart.total_discount;

      const holder = row.querySelector('[data-gwp-discount-chips]');
      if (!holder || holder.dataset.codes === key) return;
      holder.dataset.codes = key;

      holder.replaceChildren(
        ...codes.map((d) => {
          const chip = document.createElement('div');
          chip.className = `gwp-discount-chip${d.applicable ? '' : ' is-invalid'}`;
          if (!d.applicable) chip.title = 'Not valid for these items';
          chip.append(d.code);

          const remove = document.createElement('button');
          remove.type = 'button';
          remove.className = 'gwp-discount-chip__remove';
          remove.dataset.gwpRemoveCode = d.code;
          remove.setAttribute('aria-label', `Remove discount ${d.code}`);
          remove.textContent = '×';
          chip.append(remove);

          return chip;
        }),
      );
    });
  }

  async function updateCartUI(cart) {
    const containers = document.querySelectorAll('#gwp-progress-bar');
    cart = cart ?? (await fetchCart());
    renderDiscountChips(cart);
    const tiers = activeTiers();
    const spend = qualifyingSpend(cart);

    if (tiers.length) updateShippingRow(spend, tiers);
    if (!containers.length) return;

    if (!tiers.length || cart.items.length === 0) {
      containers.forEach((c) => {
        c.innerHTML = '';
        c.classList.remove('is-complete');
      });
      return;
    }

    containers.forEach((c) => applyBar(c, spend, tiers, cart));
  }

  async function rerenderDrawer() {
    const data = await nativeFetch(SECTIONS_URL).then((r) => r.json());

    const cartDrawerEl = document.querySelector('#CartDrawer');
    if (cartDrawerEl && data['cart-drawer']) {
      const doc = new DOMParser().parseFromString(data['cart-drawer'], 'text/html');
      const newContent = doc.querySelector('#CartDrawer');
      if (newContent) {
        const liveBar = cartDrawerEl.querySelector('#gwp-progress-bar');
        const freshBar = newContent.querySelector('#gwp-progress-bar');
        if (liveBar && freshBar && liveBar.innerHTML.trim()) {
          freshBar.innerHTML = liveBar.innerHTML;
          freshBar.className = liveBar.className;
        }
        updateCartMarkup(cartDrawerEl, newContent.innerHTML);
      }

      const liveDrawer = drawerElement();
      const freshDrawer = doc.querySelector('cart-drawer');
      if (liveDrawer && freshDrawer) {
        liveDrawer.classList.toggle('is-empty', freshDrawer.classList.contains('is-empty'));
      }
    }

    const bubbleEl = document.getElementById('cart-icon-bubble');
    if (bubbleEl && data['cart-icon-bubble']) {
      const doc = new DOMParser().parseFromString(data['cart-icon-bubble'], 'text/html');
      const newBubble = doc.getElementById('cart-icon-bubble');
      if (newBubble) updateCartMarkup(bubbleEl, newBubble.innerHTML);
    }

    applyGiftCopy();
    if (lastChipCart) renderDiscountChips(lastChipCart);
  }

  function cartSignature(cart) {
    const items = cart.items.map((i) => `${i.key}:${i.quantity},`).join('');
    return `${cart.total_price}|${cart.total_discount}|${items}`;
  }

  function drawerShows(cart) {
    const el = document.getElementById('gwp-product-gifts');
    return Boolean(el) && el.dataset.cartSig === cartSignature(cart);
  }

  function drawerElement() {
    return document.querySelector('cart-drawer');
  }

  function openDrawer() {
    const drawer = drawerElement();
    if (drawer && typeof drawer.open === 'function' && !drawer.classList.contains('active')) {
      drawer.open();
    }
  }

  document.addEventListener('click', (event) => {
    if (!event.target.closest?.('#CartDrawer-Overlay')) return;
    drawerElement()?.close();
  });

  // ── Scheduling ─────────────────────────────────────────────────────────
  // Events arriving mid-sync are coalesced into one more pass instead of being
  // dropped
  let syncing = false;
  let queued = false;

  async function onCartChange() {
    if (syncing) {
      queued = true;
      return;
    }
    syncing = true;
    try {
      do {
        queued = false;
        try {
          const [, cart] = await Promise.all([refreshTierAvailability(), fetchCart()]);
          await updateCartUI(cart);

          if (drawerShows(cart)) {
            drawerElement()?.classList.toggle('is-empty', cart.item_count === 0);
            applyGiftCopy();
          } else {
            await rerenderDrawer();
          }
          reorderProductGiftRows();

          let latest = cart;
          const giftsChanged = await syncGifts(cart);
          if (giftsChanged) latest = await fetchCart();

          const wwtChanged = await reconcileWwt(latest);
          if (wwtChanged) latest = await fetchCart();

          const codesCleared = await clearCodesOnEmptyCart(latest);
          if (codesCleared) latest = await fetchCart();

          if (giftsChanged || wwtChanged || codesCleared) {
            await rerenderDrawer();
            reorderProductGiftRows();
          }

          await updateCartUI(latest);
        } catch (e) {
          console.error('[gwp] sync failed', e);
        }
      } while (queued);
    } finally {
      syncing = false;
    }
  }

  async function clearCodesOnEmptyCart(cart) {
    if (cart.item_count !== 0) return false;

    document.cookie = 'discount_code=; path=/; max-age=0';
    try {
      window.sessionStorage.removeItem('auto-discount');
    } catch {}

    if (!getCartDiscountCodes(cart).length) return false;
    await updateCart({ discount: '' });
    return true;
  }

  // ── WWT code-gated gift ────────────────────────────────────────────────
  // Carried over from prod's cart-gwp.js, minus AMP and the old threshold rules.
  // A WWT-* code is a Buy X Get Y discount whose Y is the tin. Shopify only
  // discounts a Y already in the cart, so exactly one tagged tin is added before
  // the code is applied, kept at qty 1, and removed with the code. Pricing stays
  // with the discount; the tin row is locked in cart-drawer.liquid.
  const WWT = {
    promoPrefix: 'WWT-',
    giftVariantId: '56444556050598',
    discountBoxSelector: '.gwp-discount',
    discountFormSelector: '[data-gwp-discount]',
    discountInputSelector: '.gwp-discount__input',
    sessionKey: 'woobles-wwt-gwp-active',
  };

  const WWT_VERIFY_TRIES = 10;
  const WWT_VERIFY_DELAY_MS = 450;
  const WWT_BACKGROUND_VERIFY_DELAY_MS = 300;

  const wwtGiftVariantId = String(WWT.giftVariantId);

  const CART_ENDPOINTS = {
    get: CART_URL,
    add: ADD_URL,
    update: UPDATE_URL,
    checkout: `${root}checkout`,
  };

  async function requestJson(url, options = {}) {
    const response = await nativeFetch(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      ...options,
      headers: {
        Accept: 'application/json',
        ...(options.headers || {}),
      },
    });

    let data = null;

    try {
      data = await response.json();
    } catch {}

    if (!response.ok) {
      const detail = data?.description || data?.message || '';
      const suffix = detail ? `: ${detail}` : '';
      throw new Error(`${url} responded with ${response.status}${suffix}`);
    }

    return data;
  }

  let cartMutationTail = Promise.resolve();

  function enqueueCartMutation(operation) {
    const queued = cartMutationTail.then(operation, operation);
    cartMutationTail = queued.catch(() => undefined);
    return queued;
  }

  async function fetchStableCart() {
    await cartMutationTail;
    return requestJson(CART_ENDPOINTS.get);
  }

  function addItems(items) {
    const normalizedItems = (items || [])
      .map((item) => ({
        ...item,
        id: Number(item.id),
        quantity: Math.max(1, Number(item.quantity || 1)),
      }))
      .filter((item) => Number.isFinite(item.id) && item.id > 0);

    if (!normalizedItems.length) return Promise.resolve(null);

    return enqueueCartMutation(() =>
      requestJson(CART_ENDPOINTS.add, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: normalizedItems }),
      }),
    );
  }

  function updateCart({ updates = null, discount = undefined } = {}) {
    const body = {};

    if (updates && Object.keys(updates).length) {
      body.updates = updates;
    }

    if (discount !== undefined) {
      body.discount = discount;
    }

    if (!Object.keys(body).length) {
      return Promise.resolve(null);
    }

    return enqueueCartMutation(() =>
      requestJson(CART_ENDPOINTS.update, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }),
    );
  }

  function getCartItems(cart) {
    return Array.isArray(cart?.items) ? cart.items : [];
  }

  function getLinesByVariant(cart, variantId) {
    const target = String(variantId);
    return getCartItems(cart).filter((item) => String(item.variant_id) === target);
  }

  function isGiftOnlyLine(item) {
    return Boolean(giftKind(item)) || String(item?.variant_id || '') === wwtGiftVariantId;
  }

  function hasQualifyingMerchandise(cart) {
    return getCartItems(cart).some((item) => {
      if (isGiftOnlyLine(item)) return false;
      return Number(item.quantity || 0) > 0;
    });
  }

  function mergeLineUpdate(updates, lineKey, quantity) {
    if (!lineKey) return;
    updates[String(lineKey)] = Number(quantity);
  }

  function normalizeCode(code) {
    return String(code || '')
      .trim()
      .toUpperCase();
  }

  function setWwtSessionCode(code) {
    try {
      const normalized = normalizeCode(code);

      if (normalized) {
        window.sessionStorage.setItem(WWT.sessionKey, normalized);
      } else {
        window.sessionStorage.removeItem(WWT.sessionKey);
      }
    } catch {}
  }

  function getWwtSessionCode() {
    try {
      return normalizeCode(window.sessionStorage.getItem(WWT.sessionKey));
    } catch {
      return '';
    }
  }

  function isWwtCode(code) {
    return normalizeCode(code).startsWith(WWT.promoPrefix);
  }

  function getCartDiscountCodes(cart) {
    return Array.isArray(cart?.discount_codes) ? cart.discount_codes : [];
  }

  function getAppliedDiscountCodeStrings(cart) {
    return getCartDiscountCodes(cart)
      .map((discount) => normalizeCode(discount?.code))
      .filter(Boolean);
  }

  function getNonWwtDiscountCodes(cart) {
    return getAppliedDiscountCodeStrings(cart).filter((code) => !isWwtCode(code));
  }

  function hasAnyWwtDiscountCode(cart) {
    return getAppliedDiscountCodeStrings(cart).some(isWwtCode);
  }

  function hasAnyInvalidWwtDiscountCode(cart) {
    return getCartDiscountCodes(cart).some((discount) => {
      const code = normalizeCode(discount?.code);
      return isWwtCode(code) && discount?.applicable !== true;
    });
  }

  function getValidWwtDiscountCode(cart) {
    const discount = getCartDiscountCodes(cart).find((entry) => {
      const code = normalizeCode(entry?.code);
      return entry?.applicable === true && isWwtCode(code);
    });

    return normalizeCode(discount?.code);
  }

  function getWwtGiftLines(cart) {
    return getLinesByVariant(cart, wwtGiftVariantId);
  }

  function isOfficialWwtGiftLine(item) {
    return String(item?.variant_id) === wwtGiftVariantId && item?.properties?._wwt_code_gated_gift === 'true';
  }

  function getOfficialWwtGiftLines(cart) {
    return getWwtGiftLines(cart).filter(isOfficialWwtGiftLine);
  }

  function getOfficialWwtGiftForCode(cart, code) {
    const normalized = normalizeCode(code);

    return getOfficialWwtGiftLines(cart).find(
      (item) => normalizeCode(item?.properties?._wwt_discount_code) === normalized,
    );
  }

  function giftLineIsDiscounted(item) {
    if (!item) return false;

    const original = Number(item.original_line_price || 0);
    const final = Number(item.final_line_price || 0);

    return original > 0 && final < original;
  }

  function hasValidWwtGiftState(cart, expectedCode = '') {
    const validCode = getValidWwtDiscountCode(cart);
    const normalizedExpected = normalizeCode(expectedCode);

    if (!validCode) return false;
    if (normalizedExpected && validCode !== normalizedExpected) return false;
    if (!hasQualifyingMerchandise(cart)) return false;

    const giftLine = getOfficialWwtGiftForCode(cart, validCode);

    if (!giftLine) return false;
    if (Number(giftLine.quantity || 0) !== 1) return false;

    const totalWwtGiftQuantity = getWwtGiftLines(cart).reduce((sum, item) => sum + Number(item.quantity || 0), 0);

    if (totalWwtGiftQuantity !== 1) return false;

    return giftLineIsDiscounted(giftLine);
  }

  function buildWwtGiftItem(code) {
    const normalizedCode = normalizeCode(code);

    return {
      id: Number(wwtGiftVariantId),
      quantity: 1,
      properties: {
        _wwt_code_gated_gift: 'true',
        _wwt_discount_prefix: WWT.promoPrefix,
        _wwt_discount_code: normalizedCode,
      },
    };
  }

  function buildRemoveWwtGiftUpdates(cart) {
    const updates = {};

    getWwtGiftLines(cart).forEach((item) => {
      mergeLineUpdate(updates, item.key, 0);
    });

    return updates;
  }

  function buildNormalizeWwtGiftUpdates(cart, code) {
    const updates = {};
    const normalizedCode = normalizeCode(code);
    const matchingOfficialLines = getOfficialWwtGiftLines(cart).filter(
      (item) => normalizeCode(item?.properties?._wwt_discount_code) === normalizedCode,
    );

    const primary = matchingOfficialLines[0] || null;

    if (!primary) {
      getWwtGiftLines(cart).forEach((item) => {
        mergeLineUpdate(updates, item.key, 0);
      });

      return {
        updates,
        hasPrimary: false,
      };
    }

    if (Number(primary.quantity || 0) !== 1) {
      mergeLineUpdate(updates, primary.key, 1);
    }

    getWwtGiftLines(cart).forEach((item) => {
      if (item.key === primary.key) return;
      mergeLineUpdate(updates, item.key, 0);
    });

    return {
      updates,
      hasPrimary: true,
    };
  }

  function discountStringPreservingNonWwt(cart, wwtCode = '') {
    const codes = getNonWwtDiscountCodes(cart);
    const normalizedWwt = normalizeCode(wwtCode);

    if (normalizedWwt) codes.push(normalizedWwt);

    return [...new Set(codes)].join(',');
  }

  async function waitForValidWwtState(code, initialCart = null, maxTries = WWT_VERIFY_TRIES) {
    const normalizedCode = normalizeCode(code);
    let cart = initialCart;

    if (cart && hasValidWwtGiftState(cart, normalizedCode)) {
      return cart;
    }

    for (let attempt = 0; attempt < maxTries; attempt += 1) {
      await sleep(WWT_VERIFY_DELAY_MS);
      cart = await fetchStableCart();

      if (hasValidWwtGiftState(cart, normalizedCode)) {
        return cart;
      }
    }

    return cart || fetchStableCart();
  }

  function getDiscountBox(form) {
    if (!form) return null;
    return form.closest(WWT.discountBoxSelector) || form.parentElement;
  }

  function getDiscountFormMessageEl(form) {
    if (!form) return null;

    const box = getDiscountBox(form);
    if (!box) return null;

    let message = box.querySelector('.wwt-code-gwp-message');

    if (!message) {
      message = document.createElement('div');
      message.className = 'wwt-code-gwp-message';
      message.setAttribute('role', 'status');
      message.setAttribute('aria-live', 'polite');
    }

    if (message.parentElement !== box || message.previousElementSibling !== form) {
      form.insertAdjacentElement('afterend', message);
    }

    return message;
  }

  function setWwtMessage(form, text, type = 'info') {
    const message = getDiscountFormMessageEl(form);
    if (!message) return;

    message.textContent = text || '';
    message.dataset.type = type;
  }

  function setWwtFormBusy(form, busy) {
    if (!form) return;

    const button = form.querySelector("button[type='submit'], button");
    const input = form.querySelector(WWT.discountInputSelector);

    if (input) {
      input.readOnly = Boolean(busy);
      input.setAttribute('aria-busy', busy ? 'true' : 'false');
    }

    setDiscountButtonBusy(button, busy);
  }

  function setDiscountButtonBusy(button, busy) {
    if (!button) return;
    button.disabled = Boolean(busy);
    button.classList.toggle('is-loading', Boolean(busy));
    button.closest('.gwp-discount-box')?.classList.toggle('is-busy', Boolean(busy));
    button.setAttribute('aria-busy', busy ? 'true' : 'false');
    button.textContent = busy ? 'Applying...' : 'Apply';
  }

  let exclusiveTransactionInFlight = false;
  let reconcileRequestedDuringTransaction = false;

  async function runExclusiveTransaction(operation) {
    exclusiveTransactionInFlight = true;

    try {
      return await operation();
    } finally {
      exclusiveTransactionInFlight = false;

      if (reconcileRequestedDuringTransaction) {
        reconcileRequestedDuringTransaction = false;
        onCartChange();
      }
    }
  }

  async function cleanWwtState(cart, { clearCodes = true } = {}) {
    const updates = buildRemoveWwtGiftUpdates(cart);
    const hasUpdates = Object.keys(updates).length > 0;
    const shouldClearCodes = clearCodes && hasAnyWwtDiscountCode(cart);

    if (!hasUpdates && !shouldClearCodes) {
      if (clearCodes) setWwtSessionCode('');
      return cart;
    }

    const cleanedCart =
      (await updateCart({
        updates: hasUpdates ? updates : null,
        discount: shouldClearCodes ? discountStringPreservingNonWwt(cart) : undefined,
      })) || cart;

    if (clearCodes) setWwtSessionCode('');
    return cleanedCart;
  }

  async function ensureWwtGiftLine(cart, code) {
    const normalizedCode = normalizeCode(code);
    const plan = buildNormalizeWwtGiftUpdates(cart, normalizedCode);
    let workingCart = cart;

    if (Object.keys(plan.updates).length) {
      workingCart = (await updateCart({ updates: plan.updates })) || workingCart;
    }

    if (!plan.hasPrimary) {
      await addItems([buildWwtGiftItem(normalizedCode)]);
    }

    return workingCart;
  }

  async function applyWwtCodeAndValidate(cart, code, { fullVerification = true } = {}) {
    const normalizedCode = normalizeCode(code);
    const discount = discountStringPreservingNonWwt(cart, normalizedCode);

    let updatedCart = await updateCart({ discount });

    if (updatedCart && hasValidWwtGiftState(updatedCart, normalizedCode)) {
      return updatedCart;
    }

    if (!fullVerification) {
      await sleep(WWT_BACKGROUND_VERIFY_DELAY_MS);
      updatedCart = await fetchStableCart();
      return updatedCart;
    }

    return waitForValidWwtState(normalizedCode, updatedCart, WWT_VERIFY_TRIES);
  }

  async function repairWwtState(cart, code, { fullVerification = false } = {}) {
    const normalizedCode = normalizeCode(code);

    if (!normalizedCode || !hasQualifyingMerchandise(cart)) {
      return {
        cart: await cleanWwtState(cart, { clearCodes: true }),
        valid: false,
        invalidCodeObserved: hasAnyInvalidWwtDiscountCode(cart),
      };
    }

    let workingCart = await ensureWwtGiftLine(cart, normalizedCode);
    workingCart = await applyWwtCodeAndValidate(workingCart, normalizedCode, { fullVerification });

    if (hasValidWwtGiftState(workingCart, normalizedCode)) {
      setWwtSessionCode(normalizedCode);

      return {
        cart: workingCart,
        valid: true,
        invalidCodeObserved: false,
      };
    }

    const invalidCodeObserved = hasAnyInvalidWwtDiscountCode(workingCart);

    return {
      cart: await cleanWwtState(workingCart, { clearCodes: true }),
      valid: false,
      invalidCodeObserved,
    };
  }

  async function reconcileWwt(cart) {
    if (exclusiveTransactionInFlight) {
      reconcileRequestedDuringTransaction = true;
      return false;
    }

    const validWwtCode = getValidWwtDiscountCode(cart);
    const wwtGiftLines = getWwtGiftLines(cart);
    const hasMerchandise = hasQualifyingMerchandise(cart);
    const rememberedWwtCode = getWwtSessionCode();
    const appliedWwtCodes = new Set(getAppliedDiscountCodeStrings(cart));

    const recoverableRememberedWwtCode =
      hasMerchandise && isWwtCode(rememberedWwtCode) && appliedWwtCodes.has(rememberedWwtCode) ? rememberedWwtCode : '';

    const wwtEntitlementCode = validWwtCode || recoverableRememberedWwtCode;

    if (validWwtCode && hasValidWwtGiftState(cart, validWwtCode)) {
      setWwtSessionCode(validWwtCode);
    } else if (!wwtEntitlementCode && !wwtGiftLines.length) {
      setWwtSessionCode('');
    }

    const combinedUpdates = {};
    let wwtNeedsRecoveryAdd = false;
    let wwtCodeForRecovery = '';
    let clearWwtCodesInBatch = false;

    if (!wwtEntitlementCode || !hasMerchandise) {
      setWwtSessionCode('');
      wwtGiftLines.forEach((item) => mergeLineUpdate(combinedUpdates, item.key, 0));

      clearWwtCodesInBatch = hasAnyWwtDiscountCode(cart);
    } else {
      const wwtPlan = buildNormalizeWwtGiftUpdates(cart, wwtEntitlementCode);

      Object.entries(wwtPlan.updates).forEach(([key, quantity]) => {
        mergeLineUpdate(combinedUpdates, key, quantity);
      });

      if (!wwtPlan.hasPrimary) {
        wwtNeedsRecoveryAdd = true;
        wwtCodeForRecovery = wwtEntitlementCode;
      }
    }

    let cartChanged = false;
    const hasCombinedUpdates = Object.keys(combinedUpdates).length > 0;

    if (hasCombinedUpdates || clearWwtCodesInBatch) {
      cart =
        (await updateCart({
          updates: hasCombinedUpdates ? combinedUpdates : null,
          discount: clearWwtCodesInBatch ? discountStringPreservingNonWwt(cart) : undefined,
        })) || cart;

      cartChanged = true;
    }

    if (wwtNeedsRecoveryAdd && wwtCodeForRecovery) {
      const stillValidCode = getValidWwtDiscountCode(cart);
      const recoveryCode = stillValidCode || wwtCodeForRecovery;

      await repairWwtState(cart, recoveryCode, { fullVerification: false });
      cartChanged = true;
    } else if (wwtEntitlementCode && hasMerchandise && !hasValidWwtGiftState(cart, wwtEntitlementCode)) {
      await repairWwtState(cart, wwtEntitlementCode, { fullVerification: false });
      cartChanged = true;
    }

    return cartChanged;
  }

  async function handleWwtDiscountSubmit(event) {
    const form = event.target?.closest?.(WWT.discountFormSelector);
    if (!form) return;

    const input = form.querySelector(WWT.discountInputSelector);
    const code = normalizeCode(input?.value);

    if (!isWwtCode(code)) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation?.();

    if (exclusiveTransactionInFlight) {
      setWwtMessage(form, 'Please wait while your promo finishes updating.', 'info');
      return;
    }

    setWwtFormBusy(form, true);
    setWwtMessage(form, 'Adding your promo item...', 'info');

    let message = null;

    try {
      await runExclusiveTransaction(async () => {
        let cart = await fetchStableCart();

        if (!hasQualifyingMerchandise(cart)) {
          cart = await cleanWwtState(cart, { clearCodes: true });
          message = ['Add any product to your cart to use this code.', 'error'];
          return;
        }

        if (hasValidWwtGiftState(cart, code)) {
          setWwtSessionCode(code);
          message = ['Promo item unlocked!', 'success'];
          return;
        }

        setWwtMessage(form, 'Applying code...', 'info');

        const result = await repairWwtState(cart, code, {
          fullVerification: true,
        });

        if (!result.valid) {
          message = result.invalidCodeObserved
            ? ['Invalid code.', 'error']
            : ['That code could not be applied.', 'error'];
          return;
        }

        message = ['Promo item unlocked!', 'success'];
      });
    } catch (submitError) {
      console.warn('[gwp] WWT BXGY submit failed', submitError);

      try {
        await runExclusiveTransaction(async () => {
          const cart = await fetchStableCart();
          await cleanWwtState(cart, { clearCodes: true });
        });
      } catch (cleanupError) {
        console.error('[gwp] WWT cleanup failed after submit error', cleanupError);
      }

      message = ['Something went wrong applying this code. Please try again.', 'error'];
    } finally {
      await onCartChange();
      const liveForm = document.querySelector(WWT.discountFormSelector) || form;
      setWwtFormBusy(liveForm, false);
      if (message) setWwtMessage(liveForm, ...message);
    }
  }

  function isCheckoutTrigger(target) {
    if (!(target instanceof Element)) return false;

    const trigger = target.closest("a, button, input[type='submit']");
    if (!trigger) return false;

    const href = trigger.getAttribute('href') || '';
    const name = trigger.getAttribute('name') || '';
    const text = trigger.textContent || '';
    const classes = String(trigger.className || '');

    const haystack = `${href} ${name} ${text} ${classes}`.toLowerCase();
    return haystack.includes('/checkout') || haystack.includes('checkout');
  }

  function wwtStateMightBeActiveFromDom() {
    if (isWwtCode(getWwtSessionCode())) return true;

    if (document.querySelector(`[data-variant-id="${wwtGiftVariantId}"]`)) {
      return true;
    }

    const input = document.querySelector(`#CartDrawer ${WWT.discountInputSelector}`);
    if (input && isWwtCode(input.value)) return true;

    const drawer = document.getElementById('CartDrawer');

    if (drawer) {
      const discountText = [...drawer.querySelectorAll('.discounts, .gwp-discount__codes')]
        .map((element) => element.textContent || '')
        .join(' ')
        .toUpperCase();

      if (discountText.includes(WWT.promoPrefix)) return true;
    }

    return false;
  }

  async function handleCheckoutClick(event) {
    if (!isCheckoutTrigger(event.target)) return;
    if (!wwtStateMightBeActiveFromDom() && !exclusiveTransactionInFlight) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation?.();

    const form = document.querySelector(WWT.discountFormSelector);

    if (exclusiveTransactionInFlight) {
      setWwtMessage(form, 'Please wait while your code finishes applying.', 'info');
      return;
    }

    let removed = false;

    try {
      await runExclusiveTransaction(async () => {
        let cart = await fetchStableCart();
        const validCode = getValidWwtDiscountCode(cart);
        const giftLines = getWwtGiftLines(cart);
        const hasMerchandise = hasQualifyingMerchandise(cart);
        const rememberedCode = getWwtSessionCode();
        const appliedCodes = new Set(getAppliedDiscountCodeStrings(cart));
        const recoverableRememberedCode =
          hasMerchandise && isWwtCode(rememberedCode) && appliedCodes.has(rememberedCode) ? rememberedCode : '';
        const entitlementCode = validCode || recoverableRememberedCode;

        if (!entitlementCode && !giftLines.length && !hasAnyWwtDiscountCode(cart)) {
          setWwtSessionCode('');
          window.location.assign(CART_ENDPOINTS.checkout);
          return;
        }

        if (entitlementCode && hasMerchandise) {
          if (hasValidWwtGiftState(cart, entitlementCode)) {
            setWwtSessionCode(entitlementCode);
            window.location.assign(CART_ENDPOINTS.checkout);
            return;
          }

          const result = await repairWwtState(cart, entitlementCode, {
            fullVerification: true,
          });

          cart = result.cart;

          if (result.valid) {
            window.location.assign(CART_ENDPOINTS.checkout);
            return;
          }
        }

        await cleanWwtState(cart, { clearCodes: true });
        removed = true;
      });
      if (!removed) return;
      await onCartChange();
      setWwtMessage(
        document.querySelector(WWT.discountFormSelector),
        'The promo item was removed because the code is not valid.',
        'error',
      );
    } catch (checkoutError) {
      console.error('[gwp] WWT checkout validation failed', checkoutError);
      setWwtMessage(form, 'Please try checkout again.', 'error');
    }
  }

  document.addEventListener('submit', handleWwtDiscountSubmit, true);
  document.addEventListener('click', handleCheckoutClick, true);

  document.addEventListener('click', async (event) => {
    const button = event.target.closest?.('[data-gwp-remove-code]');
    if (!button || button.disabled) return;

    event.preventDefault();
    button.disabled = true;
    const code = normalizeCode(button.dataset.gwpRemoveCode);

    try {
      await runExclusiveTransaction(async () => {
        const cart = await fetchStableCart();
        const remaining = getAppliedDiscountCodeStrings(cart).filter((c) => c !== code);

        if (isWwtCode(code)) {
          setWwtSessionCode('');
          await updateCart({ updates: buildRemoveWwtGiftUpdates(cart), discount: remaining.join(',') });
        } else {
          await updateCart({ discount: remaining.join(',') });
        }
      });
    } catch (e) {
      console.error('[gwp] removing discount failed', e);
    }

    await onCartChange();
  });

  // Applied in the background only; the shopper never leaves the page
  async function applyDiscount(code) {
    const url = `${root}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(root)}`;
    try {
      await nativeFetch(url, { credentials: 'same-origin' });
      const cart = await fetchCart();
      const entered = normalizeCode(code);
      const match = getCartDiscountCodes(cart).find((d) => normalizeCode(d?.code) === entered);
      if (match?.applicable) return true;
      if (match) {
        const remaining = getAppliedDiscountCodeStrings(cart).filter((c) => c !== entered);
        await updateCart({ discount: remaining.join(',') });
      }
      return false;
    } catch (e) {
      console.error('[gwp] discount failed', e);
      return false;
    }
  }

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest?.('[data-gwp-discount]');
    if (!form) return;
    event.preventDefault();

    const input = form.querySelector('.gwp-discount__input');
    const code = input?.value.trim();
    if (!code || isWwtCode(code)) return;

    const button = form.querySelector('.gwp-discount__button');
    setDiscountButtonBusy(button, true);
    setWwtMessage(form, 'Applying code...', 'info');
    if (await applyDiscount(code)) {
      await onCartChange();
      setWwtMessage(form, '');
    } else {
      await onCartChange();
      setWwtMessage(form, 'Invalid discount code', 'error');
    }
    setDiscountButtonBusy(button, false);
  });

  document.addEventListener('input', (event) => {
    const input = event.target.closest?.('[data-gwp-discount] .gwp-discount__input');
    if (input) setWwtMessage(input.form, '');
  });

  function removeSuggestion(button) {
    button.closest('[data-gwp-upsell]')?.remove();
    const item = button.closest('[data-gwp-suggestion]');
    if (!item) return;
    const block = item.closest('.gwp-suggestions');
    item.remove();
    if (block && !block.querySelector('[data-gwp-suggestion]')) block.remove();
  }

  document.addEventListener('click', async (event) => {
    const button = event.target.closest?.('[data-gwp-add]');
    if (!button || button.disabled) return;

    event.preventDefault();
    const variantId = Number(button.dataset.variantId);
    if (!Number.isFinite(variantId) || variantId <= 0) return;

    button.disabled = true;
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
    try {
      await postJSON(ADD_URL, { items: [{ id: variantId, quantity: 1 }] });
      await onCartChange();
      if (Number(button.dataset.variantId) === variantId) removeSuggestion(button);
      openDrawer();
    } catch (e) {
      console.error('[gwp] add failed', e);
      button.disabled = false;
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
    }
  });

  function setFormAddBusy(form, button, busy) {
    form.toggleAttribute('data-gwp-adding', busy);
    if (!button) return;
    button.classList.toggle('loading', busy);
    button.toggleAttribute('aria-busy', busy);
    if (busy) button.setAttribute('aria-disabled', 'true');
    else button.removeAttribute('aria-disabled');
    button.querySelector('.loading__spinner')?.classList.toggle('hidden', !busy);
  }

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (event.defaultPrevented || !(form instanceof HTMLFormElement)) return;
    if (!/\/cart\/add(\.js)?$/.test(new URL(form.action, location.href).pathname)) return;
    if (!drawerElement()) return;

    event.preventDefault();
    if (form.hasAttribute('data-gwp-adding')) return;

    const button = event.submitter || form.querySelector('[type="submit"]');
    setFormAddBusy(form, button, true);
    try {
      const response = await nativeFetch(ADD_URL, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });
      if (!response.ok) throw new Error(`cart/add ${response.status}`);
      await onCartChange();
      openDrawer();
      document.dispatchEvent(new CustomEvent('cart:updated'));
    } catch (e) {
      console.error('[gwp] form add failed', e);
      form.dispatchEvent(new CustomEvent('product-form:error', { bubbles: true }));
    } finally {
      setFormAddBusy(form, button, false);
    }
  });

  let interceptTimer = null;
  let openAfterIntercept = false;

  function scheduleFromIntercept(isCartAdd) {
    if (isCartAdd) openAfterIntercept = true;
    clearTimeout(interceptTimer);
    interceptTimer = setTimeout(() => {
      onCartChange();
      if (openAfterIntercept) openDrawer();
      openAfterIntercept = false;
    }, FETCH_INTERCEPT_DELAY_MS);
  }

  window.fetch = function (...args) {
    const url = args[0]?.url ?? args[0]?.toString() ?? '';
    const cartMutationMatch = url.match(/\/cart\/(add|change|update|clear)(\.js)?(\?|$)/);
    const isCartMutation = Boolean(cartMutationMatch) && !url.includes('sections=');
    const isCartAdd = isCartMutation && cartMutationMatch[1] === 'add';

    const promise = nativeFetch.apply(this, args);
    if (isCartMutation) {
      promise.then(() => scheduleFromIntercept(isCartAdd)).catch(() => {});
    }
    return promise;
  };

  let knownBar = document.getElementById('gwp-progress-bar');
  let knownLines = document.getElementById('CartDrawer-CartItems');
  const observer = new MutationObserver(() => {
    const lines = document.getElementById('CartDrawer-CartItems');
    if (lines && lines !== knownLines) {
      knownLines = lines;
      reorderProductGiftRows();
    }

    const bar = document.getElementById('gwp-progress-bar');
    if (!bar || bar === knownBar) return;
    knownBar = bar;

    restoreBar(bar);

    if (syncing) return;
    onCartChange();
  });
  observer.observe(document.body, { childList: true, subtree: true });

  // A morphed render keeps the same elements, so the observer above misses it.
  document.addEventListener('cart:morphed', () => {
    reorderProductGiftRows();
    applyGiftCopy();
    if (lastChipCart) renderDiscountChips(lastChipCart);
  });

  reorderProductGiftRows();
  onCartChange();
})();
